/**
 * Collision check behind `ReviewPanel`'s `preferCompactQuote` prop: the
 * desktop staff-quote panel is a fixed-content-size box (avatar/quote/
 * attribution/CTAs at fixed font sizes and gaps) bottom-anchored over an
 * image whose own height shrinks with viewport width (`aspect-ratio` in
 * `ImageHotspot.module.scss`). At the Figma-reference 1440px width the panel
 * is a small fraction of the image; at narrower "desktop" widths (still
 * above the `md` breakpoint, so the mobile layout hasn't kicked in) it can
 * grow to take up half the image or more, visually covering — and making
 * unclickable — a hotspot dot or the top-left tab bar authored underneath
 * where it happens to render. `ImageHotspot` measures the real, mounted
 * elements via `getBoundingClientRect()` and calls `shouldPreferCompactQuote`
 * to decide whether to fall back to the mobile collapsed-trigger/reveal-card
 * treatment instead, purely from geometry — no fixed breakpoint tier, so it
 * stays correct regardless of quote length, hotspot placement, or tab count.
 */

import { frameToContainerY } from './frameMapping';

export interface Rect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Matches `.dot`'s rendered diameter at desktop widths
    (`HotspotDot.module.scss`'s `@include respond(md) { width/height: rem(36px) }`)
    — an approximate hit-box for the check below, not used for rendering, so
    a few px of slack either way is harmless. */
export const HOTSPOT_DOT_DIAMETER_PX = 36;

export const rectsOverlap = (a: Rect, b: Rect): boolean =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/** A hotspot's authored `x`/`y` are percentages of the image container —
    resolve one to its approximate on-screen circle, given that container's
    own rect. `frameRatio` (see `frameMapping.ts`) accounts for the
    viewport height cap cropping the frame — omitted, the container is
    treated as the uncropped frame itself (`y%` of its height). */
export const hotspotDotRect = (
  hotspot: { x: number; y: number },
  containerRect: Rect,
  dotDiameter: number = HOTSPOT_DOT_DIAMETER_PX,
  frameRatio?: number
): Rect => {
  const width = containerRect.right - containerRect.left;
  const height = containerRect.bottom - containerRect.top;
  const half = dotDiameter / 2;
  const centerX = containerRect.left + (hotspot.x / 100) * width;
  const centerY =
    containerRect.top +
    frameToContainerY(
      hotspot.y,
      { width, height },
      frameRatio ?? height / width,
      frameRatio === undefined ? 0 : half
    );
  return {
    left: centerX - half,
    top: centerY - half,
    right: centerX + half,
    bottom: centerY + half,
  };
};

export const shouldPreferCompactQuote = ({
  panelRect,
  containerRect,
  hotspots,
  navOverlayRect,
  dotDiameter = HOTSPOT_DOT_DIAMETER_PX,
  frameRatio,
}: {
  /** The desktop quote panel's own measured rect — `null`/zero-sized (not
      actually rendered, e.g. no quote authored) is treated as "nothing to
      avoid". */
  panelRect: Rect | null;
  containerRect: Rect;
  hotspots: Array<{ x: number; y: number }>;
  /** The tab bar's own measured rect, when one is rendered (2+ tabs). */
  navOverlayRect?: Rect | null;
  dotDiameter?: number;
  /** Forwarded to `hotspotDotRect`. */
  frameRatio?: number;
}): boolean => {
  if (
    !panelRect ||
    (panelRect.right - panelRect.left === 0 &&
      panelRect.bottom - panelRect.top === 0)
  ) {
    return false;
  }

  const hitsHotspot = hotspots.some((hotspot) =>
    rectsOverlap(
      panelRect,
      hotspotDotRect(hotspot, containerRect, dotDiameter, frameRatio)
    )
  );
  const hitsNavOverlay = navOverlayRect
    ? rectsOverlap(panelRect, navOverlayRect)
    : false;

  return hitsHotspot || hitsNavOverlay;
};
